import { AppAction, UserRole } from "@edlab-fms/shared";
import { useEffect, useMemo, useState } from "react";
import { useFiscalPeriodsQuery } from "../lib/api/reference";
import { UserAdminPanel } from "../features/settings/UserAdminPanel";
import { SystemReferencePanel } from "../features/settings/SystemReferencePanel";
import { BankBalancesPanel } from "../features/settings/BankBalancesPanel";
import { CategoriesPanel } from "../features/settings/CategoriesPanel";
import { useAuthQuery } from "../lib/api/auth";
import { useBankAccountsQuery } from "../lib/api/reference";
import {
  useAuditEventsQuery,
  useClosePeriodMutation,
  useClosedPeriodsQuery,
  useCreateReconciliationMutation,
  useFinalizeReconciliationMutation,
  useReconciliationQuery,
  useReconciliationsQuery,
  useReopenPeriodMutation,
  useUpdateReconciliationItemsMutation,
  type ReconciliationDetailDto,
} from "../lib/api/settings";
import { ApiClientError } from "../lib/api/client";
import { formatMoney } from "../lib/format/money";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { EmptyState } from "../components/ui/EmptyState";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { PageHeader } from "../components/ui/PageHeader";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../components/ui/Table";

type SettingsTab =
  | "audit"
  | "reconciliation"
  | "periods"
  | "users"
  | "reference"
  | "categories"
  | "banks";

function monthLabel(value: string): string {
  const date = new Date(`${value}-01T12:00:00`);
  return date.toLocaleDateString("en-GB", { month: "short", year: "numeric" });
}

function ReconciliationDetailPanel({
  reconciliation,
  onClose,
}: {
  reconciliation: ReconciliationDetailDto;
  onClose: () => void;
}) {
  const updateItems = useUpdateReconciliationItemsMutation();
  const finalize = useFinalizeReconciliationMutation();
  const [error, setError] = useState<string | null>(null);
  const [cleared, setCleared] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(reconciliation.items.map((item) => [item.id, item.cleared])),
  );

  const isDraft = reconciliation.status === "draft";

  async function saveItems() {
    setError(null);
    try {
      await updateItems.mutateAsync({
        id: reconciliation.id,
        items: reconciliation.items.map((item) => ({
          id: item.id,
          cleared: cleared[item.id] ?? item.cleared,
        })),
      });
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "Could not save items.");
    }
  }

  async function finalizeReconciliation() {
    setError(null);
    try {
      await finalize.mutateAsync(reconciliation.id);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "Could not finalize.");
    }
  }

  return (
    <Card className="mt-4">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-semibold text-edlab-ink">
            {reconciliation.bankAccountName} · {reconciliation.statementDate}
          </h3>
          <p className="text-sm text-edlab-muted">
            Statement {formatMoney(reconciliation.statementClosingBalance)} · Book{" "}
            {formatMoney(reconciliation.bookBalance)} · Difference{" "}
            {formatMoney(reconciliation.difference)}
          </p>
        </div>
        <Button variant="secondary" onClick={onClose}>
          Close detail
        </Button>
      </div>
      {error ? (
        <p className="edlab-error mb-3">
          {error}
        </p>
      ) : null}
      <Table caption="Reconciliation items">
        <TableHead>
          <TableRow>
            {isDraft ? <TableHeaderCell>Cleared</TableHeaderCell> : null}
            <TableHeaderCell>Date</TableHeaderCell>
            <TableHeaderCell>Reference</TableHeaderCell>
            <TableHeaderCell>Description</TableHeaderCell>
            <TableHeaderCell>Amount</TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {reconciliation.items.map((item) => (
            <TableRow key={item.id}>
              {isDraft ? (
                <TableCell>
                  <input
                    type="checkbox"
                    checked={cleared[item.id] ?? item.cleared}
                    onChange={(e) => {
                      setCleared((prev) => ({ ...prev, [item.id]: e.target.checked }));
                    }}
                  />
                </TableCell>
              ) : null}
              <TableCell>{item.date}</TableCell>
              <TableCell>{item.reference}</TableCell>
              <TableCell>{item.description}</TableCell>
              <TableCell>
                {item.direction === "out" ? "−" : "+"}
                {formatMoney(item.amount)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {isDraft ? (
        <div className="mt-4 flex gap-2">
          <Button disabled={updateItems.isPending} onClick={() => void saveItems()}>
            Save cleared items
          </Button>
          <Button
            variant="secondary"
            disabled={finalize.isPending}
            onClick={() => void finalizeReconciliation()}
          >
            Finalize
          </Button>
        </div>
      ) : null}
    </Card>
  );
}

export function SettingsPage() {
  const auth = useAuthQuery();
  const user = auth.data;

  const canAudit = Boolean(user?.permissions.actions.includes(AppAction.AUDIT_READ));
  const canManage =
    user?.role === UserRole.SUPER_ADMIN || user?.role === UserRole.ACCOUNTS;
  const canManageUsers = Boolean(user?.permissions.actions.includes(AppAction.SETTINGS_MANAGE));
  const canViewBanks =
    user?.role === UserRole.SUPER_ADMIN ||
    user?.role === UserRole.ACCOUNTS ||
    user?.role === UserRole.AUDITOR;
  const canReopen = user?.role === UserRole.SUPER_ADMIN;

  const defaultTab: SettingsTab = canManageUsers
    ? "users"
    : canAudit
      ? "audit"
      : canManage
        ? "reconciliation"
        : "audit";
  const [tab, setTab] = useState<SettingsTab>(defaultTab);

  const fiscalPeriods = useFiscalPeriodsQuery(Boolean(user));
  const [auditFrom, setAuditFrom] = useState("");
  const [auditTo, setAuditTo] = useState("");
  const [auditAction, setAuditAction] = useState("");
  const [accessChangesOnly, setAccessChangesOnly] = useState(false);

  const [closeMonth, setCloseMonth] = useState("");
  const [reopenMonth, setReopenMonth] = useState("");
  const [reopenReason, setReopenReason] = useState("");

  const [selectedReconciliationId, setSelectedReconciliationId] = useState<string | null>(
    null,
  );
  const [newReconBankId, setNewReconBankId] = useState("");
  const [newReconDate, setNewReconDate] = useState("");
  const [newReconBalance, setNewReconBalance] = useState("");
  const [actionError, setActionError] = useState<string | null>(null);

  const audit = useAuditEventsQuery(
    { from: auditFrom, to: auditTo, action: auditAction || undefined, accessChangesOnly },
    Boolean(user) && tab === "audit" && canAudit,
  );
  const periods = useClosedPeriodsQuery(Boolean(user) && tab === "periods" && canManage);
  const reconciliations = useReconciliationsQuery(
    Boolean(user) && tab === "reconciliation" && canManage,
  );
  const reconciliationDetail = useReconciliationQuery(selectedReconciliationId);
  const banks = useBankAccountsQuery(tab === "reconciliation" && canManage);

  const closePeriod = useClosePeriodMutation();
  const reopenPeriod = useReopenPeriodMutation();
  const createReconciliation = useCreateReconciliationMutation();

  useEffect(() => {
    if (!fiscalPeriods.data) {
      return;
    }
    if (!auditFrom) {
      setAuditFrom(fiscalPeriods.data.startsOn);
    }
    if (!auditTo) {
      setAuditTo(fiscalPeriods.data.endsOn);
    }
    if (!closeMonth && fiscalPeriods.data.months.length > 0) {
      const priorMonth =
        fiscalPeriods.data.months[fiscalPeriods.data.months.length - 2] ??
        fiscalPeriods.data.months[0];
      setCloseMonth(priorMonth ?? fiscalPeriods.data.defaultPeriod);
    }
    if (!newReconDate) {
      setNewReconDate(fiscalPeriods.data.endsOn);
    }
  }, [fiscalPeriods.data, auditFrom, auditTo, closeMonth, newReconDate]);

  const fiscalMonths = fiscalPeriods.data?.months ?? [];

  const tabs = useMemo(() => {
    const items: { id: SettingsTab; label: string }[] = [];
    if (canManageUsers) items.push({ id: "users", label: "People & access" });
    if (canViewBanks) items.push({ id: "banks", label: "Bank & cash" });
    if (canManageUsers) items.push({ id: "categories", label: "Categories" });
    if (canManageUsers) items.push({ id: "reference", label: "Programme setup" });
    if (canAudit) items.push({ id: "audit", label: "Activity" });
    if (canManage) {
      items.push({ id: "reconciliation", label: "Bank reconciliation" });
      items.push({ id: "periods", label: "Closed periods" });
    }
    return items;
  }, [canAudit, canManage, canManageUsers, canViewBanks]);

  if (auth.isLoading) {
    return <LoadingSpinner label="Loading settings…" className="py-12" />;
  }

  if (!user || tabs.length === 0) {
    return (
      <EmptyState
        title="Settings unavailable"
        description="Your role does not include access to settings."
      />
    );
  }

  async function runAction(action: () => Promise<unknown>) {
    setActionError(null);
    try {
      await action();
    } catch (err) {
      setActionError(err instanceof ApiClientError ? err.message : "Action failed.");
    }
  }

  return (
    <div className="grid gap-6">
      <PageHeader
        eyebrow="Settings"
        title="A foundation for good records."
        description="Manage the people, categories and accounts behind your financials."
      />

      <div className="flex flex-wrap gap-2 border-b border-[#e1e6ea] pb-3">
        {tabs.map((item) => (
          <button
            key={item.id}
            type="button"
            className={tab === item.id ? "settings-tab settings-tab-active" : "settings-tab"}
            onClick={() => {
              setTab(item.id);
              setActionError(null);
            }}
          >
            {item.label}
          </button>
        ))}
      </div>

      {actionError ? <p className="edlab-error">{actionError}</p> : null}

      {tab === "users" && canManageUsers ? <UserAdminPanel /> : null}

      {tab === "categories" && canManageUsers ? <CategoriesPanel /> : null}

      {tab === "reference" && canManageUsers ? <SystemReferencePanel /> : null}

      {tab === "banks" && canViewBanks ? <BankBalancesPanel /> : null}

      {tab === "audit" && canAudit ? (
        <Card>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-edlab-ink">Activity history</h2>
            {audit.data?.events ? (
              <span className="rounded-md bg-[#eef6f1] px-2.5 py-1 text-xs font-semibold text-edlab-green">
                {audit.data.events.length} events
              </span>
            ) : null}
          </div>
          <div className="no-print mb-4 grid gap-3 md:grid-cols-4">
            <label className="grid gap-1 text-sm">
              From
              <input
                type="date"
                className="edlab-input"
                value={auditFrom}
                onChange={(e) => {
                  setAuditFrom(e.target.value);
                }}
              />
            </label>
            <label className="grid gap-1 text-sm">
              To
              <input
                type="date"
                className="edlab-input"
                value={auditTo}
                onChange={(e) => {
                  setAuditTo(e.target.value);
                }}
              />
            </label>
            <label className="grid gap-1 text-sm">
              Action contains
              <input
                className="edlab-input"
                value={auditAction}
                onChange={(e) => {
                  setAuditAction(e.target.value);
                }}
                placeholder="e.g. receipt"
              />
            </label>
            <label className="flex items-end gap-2 text-sm">
              <input
                type="checkbox"
                checked={accessChangesOnly}
                onChange={(e) => {
                  setAccessChangesOnly(e.target.checked);
                }}
              />
              Access changes only
            </label>
          </div>
          {audit.isLoading ? (
            <LoadingSpinner label="Loading activity…" />
          ) : user?.permissions.unitScope !== "all" ? (
            <p className="text-sm text-edlab-muted">
              Full activity history requires organization-wide access.
            </p>
          ) : (audit.data?.events.length ?? 0) === 0 ? (
            <EmptyState title="No events" description="No activity matches your filters." />
          ) : (
            <div className="audit-list">
              {audit.data?.events.map((event) => {
                const when = new Date(event.createdAt);
                return (
                  <div key={event.id} className="audit-line">
                    <time className="text-xs text-edlab-muted">
                      {when.toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                      })}{" "}
                      ·{" "}
                      {when.toLocaleTimeString("en-GB", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </time>
                    <div>
                      <b className="text-sm text-edlab-ink">{event.actor.name}</b>
                      <p className="mt-0.5 text-sm text-edlab-muted">{event.action}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      ) : null}

      {tab === "periods" && canManage ? (
        <div className="grid gap-4">
          <Card>
            <h3 className="mb-3 font-semibold text-edlab-ink">Close period</h3>
            <div className="flex flex-wrap items-end gap-3">
              <label className="grid gap-1 text-sm">
                Month
                <select
                  className="edlab-input"
                  value={closeMonth}
                  onChange={(e) => {
                    setCloseMonth(e.target.value);
                  }}
                >
                  {fiscalMonths.map((month) => (
                    <option key={month} value={month}>
                      {monthLabel(month)}
                    </option>
                  ))}
                </select>
              </label>
              <Button
                disabled={closePeriod.isPending}
                onClick={() =>
                  void runAction(() => closePeriod.mutateAsync(closeMonth))
                }
              >
                Close month
              </Button>
            </div>
          </Card>
          {canReopen ? (
            <Card>
              <h3 className="mb-3 font-semibold text-edlab-ink">Reopen period</h3>
              <div className="flex flex-wrap items-end gap-3">
                <label className="grid gap-1 text-sm">
                  Month
                  <select
                    className="edlab-input"
                    value={reopenMonth}
                    onChange={(e) => {
                      setReopenMonth(e.target.value);
                    }}
                  >
                    <option value="">Select closed month</option>
                    {(periods.data?.periods ?? [])
                      .filter((period) => period.status === "closed")
                      .map((period) => (
                        <option key={period.id} value={period.month}>
                          {monthLabel(period.month)}
                        </option>
                      ))}
                  </select>
                </label>
                <label className="min-w-[240px] flex-1 grid gap-1 text-sm">
                  Reason
                  <input
                    className="edlab-input"
                    value={reopenReason}
                    onChange={(e) => {
                      setReopenReason(e.target.value);
                    }}
                    placeholder="Why is this period being reopened?"
                  />
                </label>
                <Button
                  variant="secondary"
                  disabled={reopenPeriod.isPending || !reopenMonth}
                  onClick={() =>
                    void runAction(() =>
                      reopenPeriod.mutateAsync({ month: reopenMonth, reason: reopenReason }),
                    )
                  }
                >
                  Reopen
                </Button>
              </div>
            </Card>
          ) : null}
          <Card>
            <h3 className="mb-3 font-semibold text-edlab-ink">Period history</h3>
            {periods.isLoading ? (
              <LoadingSpinner label="Loading periods…" />
            ) : (periods.data?.periods.length ?? 0) === 0 ? (
              <EmptyState title="No closed periods" description="All months are open." />
            ) : (
              <Table caption="Closed periods">
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Month</TableHeaderCell>
                    <TableHeaderCell>Status</TableHeaderCell>
                    <TableHeaderCell>Closed by</TableHeaderCell>
                    <TableHeaderCell>Closed at</TableHeaderCell>
                    <TableHeaderCell>Reopened</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {periods.data?.periods.map((period) => (
                    <TableRow key={period.id}>
                      <TableCell>{monthLabel(period.month)}</TableCell>
                      <TableCell>{period.status}</TableCell>
                      <TableCell>{period.closedBy.name}</TableCell>
                      <TableCell>
                        {new Date(period.closedAt).toLocaleString("en-GB")}
                      </TableCell>
                      <TableCell>
                        {period.reopenedAt
                          ? `${new Date(period.reopenedAt).toLocaleString("en-GB")} — ${period.reopenReason}`
                          : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </div>
      ) : null}

      {tab === "reconciliation" && canManage ? (
        <div className="grid gap-4">
          <Card>
            <h3 className="mb-3 font-semibold text-edlab-ink">Start reconciliation</h3>
            <div className="grid gap-3 md:grid-cols-4">
              <label className="grid gap-1 text-sm">
                Bank account
                <select
                  className="edlab-input"
                  value={newReconBankId || banks.data?.bankAccounts[0]?.id || ""}
                  onChange={(e) => {
                    setNewReconBankId(e.target.value);
                  }}
                >
                  {(banks.data?.bankAccounts ?? []).map((bank) => (
                    <option key={bank.id} value={bank.id}>
                      {bank.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="grid gap-1 text-sm">
                Statement date
                <input
                  type="date"
                  className="edlab-input"
                  value={newReconDate}
                  onChange={(e) => {
                    setNewReconDate(e.target.value);
                  }}
                />
              </label>
              <label className="grid gap-1 text-sm">
                Closing balance (PKR)
                <input
                  type="number"
                  step="0.01"
                  className="edlab-input"
                  value={newReconBalance}
                  onChange={(e) => {
                    setNewReconBalance(e.target.value);
                  }}
                />
              </label>
              <div className="flex items-end">
                <Button
                  disabled={createReconciliation.isPending || !newReconBalance}
                  onClick={() =>
                    void runAction(async () => {
                      const result = await createReconciliation.mutateAsync({
                        bankAccountId:
                          newReconBankId || banks.data?.bankAccounts[0]?.id || "",
                        statementDate: newReconDate,
                        statementClosingBalance: Number.parseFloat(newReconBalance).toFixed(2),
                      });
                      setSelectedReconciliationId(result.reconciliation.id);
                    })
                  }
                >
                  Create draft
                </Button>
              </div>
            </div>
          </Card>
          <Card>
            <h3 className="mb-3 font-semibold text-edlab-ink">Reconciliations</h3>
            {reconciliations.isLoading ? (
              <LoadingSpinner label="Loading reconciliations…" />
            ) : (reconciliations.data?.reconciliations.length ?? 0) === 0 ? (
              <EmptyState
                title="No reconciliations"
                description="Create a draft reconciliation to match bank statement movements."
              />
            ) : (
              <Table caption="Bank reconciliations">
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Account</TableHeaderCell>
                    <TableHeaderCell>Statement date</TableHeaderCell>
                    <TableHeaderCell>Statement</TableHeaderCell>
                    <TableHeaderCell>Book</TableHeaderCell>
                    <TableHeaderCell>Difference</TableHeaderCell>
                    <TableHeaderCell>Status</TableHeaderCell>
                    <TableHeaderCell>Actions</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reconciliations.data?.reconciliations.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.bankAccountName}</TableCell>
                      <TableCell>{row.statementDate}</TableCell>
                      <TableCell>{formatMoney(row.statementClosingBalance)}</TableCell>
                      <TableCell>{formatMoney(row.bookBalance)}</TableCell>
                      <TableCell>{formatMoney(row.difference)}</TableCell>
                      <TableCell>{row.status}</TableCell>
                      <TableCell>
                        <Button
                          variant="secondary"
                          onClick={() => {
                            setSelectedReconciliationId(row.id);
                          }}
                        >
                          Open
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
          {reconciliationDetail.data?.reconciliation ? (
            <ReconciliationDetailPanel
              reconciliation={reconciliationDetail.data.reconciliation}
              onClose={() => {
                setSelectedReconciliationId(null);
              }}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
